import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Toaster } from 'react-hot-toast';

// Import components
import HomePage from './components/HomePage';
import ServiceDetailPage from './components/ServiceDetailPage';
import BlogPage from './components/BlogPage';
import BlogDetailPage from './components/BlogDetailPage';
import CareersPage from './components/CareersPage';
import JobDetailPage from './components/JobDetailPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import CookiePolicyPage from './components/CookiePolicyPage';
import NotFoundPage from './components/NotFoundPage';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';

// Import admin components
import AdminDashboard from './admin/pages/AdminDashboard';
import CompanyInfoPage from './admin/pages/CompanyInfoPage';
import MediaLibrary from './admin/pages/MediaLibrary';
import LoginPage from './admin/pages/LoginPage';
import ForgotPasswordPage from './admin/pages/ForgotPasswordPage';
import ResetPasswordPage from './admin/pages/ResetPasswordPage';
import PortfolioPage from './admin/pages/PortfolioPage';
import TeamPage from './admin/pages/TeamPage';
import ProductsPage from './admin/pages/ProductsPage';
import ChatManagementPage from './admin/pages/ChatManagementPage';
import TestimonialsPage from './admin/pages/TestimonialsPage';
import MessagesPage from './admin/pages/MessagesPage';
import BlogPostsPage from './admin/pages/BlogPostsPage';
import JobsPage from './admin/pages/JobsPage';
import JobApplicationsPage from './admin/pages/JobApplicationsPage';
import ApplicationDetailPage from './admin/pages/ApplicationDetailPage';
import PipelinePage from './admin/pages/PipelinePage';
import InterviewsPage from './admin/pages/InterviewsPage';
import RecruitmentAnalyticsPage from './admin/pages/RecruitmentAnalyticsPage';
import ServicesPage from './admin/pages/ServicesPage';
import NewsletterPage from './admin/pages/NewsletterPage';
import TechnologiesPage from './admin/pages/TechnologiesPage';
import MediaTest from './admin/components/MediaTest';
import UsersPage from './admin/pages/UsersPage';
import SessionsPage from './admin/pages/SessionsPage';
import AuditLogsPage from './admin/pages/AuditLogsPage';
import AnalyticsDashboard from './admin/pages/AnalyticsDashboard';
import EmailTemplatesPage from './admin/pages/EmailTemplatesPage';
import ChatbotResponsesPage from './admin/pages/ChatbotResponsesPage';
import CommunicationLogsPage from './admin/pages/CommunicationLogsPage';
import NotificationsManagementPage from './admin/pages/NotificationsManagementPage';
import EmailConfigPage from './admin/pages/EmailConfigPage';
import DatabaseBackupPage from './admin/pages/DatabaseBackupPage';
import UserProfilePage from './admin/pages/UserProfilePage';
import AuthGuard from './admin/components/AuthGuard';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <div className="App">
            <ScrollToTop />
            
            <Routes>
              {/* Admin routes */}
              <Route path="/admin" element={<AuthGuard><AdminDashboard /></AuthGuard>} />
              <Route path="/admin/dashboard" element={<AuthGuard><AdminDashboard /></AuthGuard>} />
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
              <Route path="/admin/company" element={<AuthGuard><CompanyInfoPage /></AuthGuard>} />
              <Route path="/admin/media" element={<AuthGuard><MediaLibrary /></AuthGuard>} />
              <Route path="/admin/portfolio" element={<AuthGuard><PortfolioPage /></AuthGuard>} />
              <Route path="/admin/team" element={<AuthGuard><TeamPage /></AuthGuard>} />
              <Route path="/admin/products" element={<AuthGuard><ProductsPage /></AuthGuard>} />
              <Route path="/admin/chat" element={<AuthGuard><ChatManagementPage /></AuthGuard>} />
              <Route path="/admin/testimonials" element={<AuthGuard><TestimonialsPage /></AuthGuard>} />
              <Route path="/admin/messages" element={<AuthGuard><MessagesPage /></AuthGuard>} />
              <Route path="/admin/blog" element={<AuthGuard><BlogPostsPage /></AuthGuard>} />
              <Route path="/admin/services" element={<AuthGuard><ServicesPage /></AuthGuard>} />
              <Route path="/admin/jobs" element={<AuthGuard><JobsPage /></AuthGuard>} />
              <Route path="/admin/job-applications" element={<AuthGuard><JobApplicationsPage /></AuthGuard>} />
              <Route path="/admin/applications" element={<AuthGuard><JobApplicationsPage /></AuthGuard>} />
              <Route path="/admin/applications/:id" element={<AuthGuard><ApplicationDetailPage /></AuthGuard>} />
              <Route path="/admin/interviews" element={<AuthGuard><InterviewsPage /></AuthGuard>} />
              <Route path="/admin/pipeline" element={<AuthGuard><PipelinePage /></AuthGuard>} />
              <Route path="/admin/recruitment-analytics" element={<AuthGuard><RecruitmentAnalyticsPage /></AuthGuard>} />
              <Route path="/admin/newsletter" element={<AuthGuard><NewsletterPage /></AuthGuard>} />
              <Route path="/admin/technologies" element={<AuthGuard><TechnologiesPage /></AuthGuard>} />
              <Route path="/admin/analytics" element={<AuthGuard><AnalyticsDashboard /></AuthGuard>} />
              <Route path="/admin/users" element={<AuthGuard><UsersPage /></AuthGuard>} />
              <Route path="/admin/sessions" element={<AuthGuard><SessionsPage /></AuthGuard>} />
              <Route path="/admin/audit-logs" element={<AuthGuard><AuditLogsPage /></AuthGuard>} />
              <Route path="/admin/email-templates" element={<AuthGuard><EmailTemplatesPage /></AuthGuard>} />
              <Route path="/admin/chatbot-responses" element={<AuthGuard><ChatbotResponsesPage /></AuthGuard>} />
              <Route path="/admin/communication-logs" element={<AuthGuard><CommunicationLogsPage /></AuthGuard>} />
              <Route path="/admin/notifications-management" element={<AuthGuard><NotificationsManagementPage /></AuthGuard>} />
              <Route path="/admin/email-config" element={<AuthGuard><EmailConfigPage /></AuthGuard>} />
              <Route path="/admin/database-backup" element={<AuthGuard requireAdmin><DatabaseBackupPage /></AuthGuard>} />
              <Route path="/admin/profile" element={<AuthGuard><UserProfilePage /></AuthGuard>} />
              <Route path="/admin/media-test" element={<AuthGuard><MediaTest /></AuthGuard>} />
              
              {/* Main website routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/services/:slug" element={<ServiceDetailPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogDetailPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/:slug" element={<JobDetailPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />

              {/* 404 - page not found */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>

            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#0A2540',
                  color: '#fff',
                },
                success: {
                  iconTheme: {
                    primary: '#FFC72C',
                    secondary: '#fff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />

            {/* React Query DevTools - only in development */}
            {process.env.NODE_ENV === 'development' && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </div>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;